import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  GitBranch,
  ShieldAlert,
  Sparkles,
  TimerReset,
} from 'lucide-react';
import { useWorkspace } from '../context/WorkspaceContext';
import { formatDate, severityLabel } from '../lib/format';
import type { ValidationFinding } from '../lib/types';
import { Avatar, Button, DetailList, Drawer, MetricDelta, PageHeader, ProgressBar, SectionHeader, Select, StatusChip, cx } from '../components/ui';

const chartPoints = '0,80 55,73 110,76 165,57 220,63 275,42 330,47 385,28 440,33 495,20 550,25 605,12';

export function OverviewPage() {
  const { data } = useWorkspace();
  const navigate = useNavigate();
  const [entity, setEntity] = useState('all');
  const [horizon, setHorizon] = useState('30');
  const [selectedFinding, setSelectedFinding] = useState<ValidationFinding | null>(null);
  if (!data) return null;

  const filteredReports = useMemo(() => data.reports.filter((report) => entity === 'all' || report.entity === entity), [data.reports, entity]);
  const blocking = data.findings.filter((finding) => finding.severity === 'blocking' || finding.severity === 'high').slice(0, 4);
  const entities = [...new Set(data.reports.map((report) => report.entity))];

  return (
    <div className="page-stack overview-page">
      <PageHeader
        eyebrow="Control Center"
        title="Guten Morgen, Leonie."
        description="Prioritäten, Blocker und Nachweisstatus Ihres regulatorischen Reportings – transparent bis zur Quelle."
        actions={<><Select label="Rechtseinheit" value={entity} onChange={setEntity}><option value="all">Alle Rechtseinheiten</option>{entities.map((name) => <option key={name}>{name}</option>)}</Select><Select label="Zeitraum" value={horizon} onChange={setHorizon}><option value="7">Nächste 7 Tage</option><option value="30">Nächste 30 Tage</option><option value="90">Nächste 90 Tage</option></Select></>}
      />

      <section className="kpi-grid" aria-label="Executive Kennzahlen">
        {data.kpis.map((kpi, index) => (
          <button key={kpi.id} className={cx('kpi-card', `kpi-${kpi.tone}`)} onClick={() => navigate(kpi.route)}>
            <div className="kpi-top"><span>{kpi.label}</span><span className="kpi-index">0{index + 1}</span></div>
            <strong className="kpi-value">{kpi.value}</strong>
            <p>{kpi.detail}</p>
            <div className="kpi-footer"><MetricDelta positive={kpi.tone === 'success' || kpi.id === 'lineage'}>{kpi.trend}</MetricDelta><ChevronRight size={16} /></div>
          </button>
        ))}
      </section>

      <section className="overview-grid">
        <div className="panel panel-wide readiness-panel">
          <SectionHeader title="Readiness-Entwicklung" description="Anteil einreichungsbereiter Reports · letzte 12 Wochen" action={<StatusChip label="Live snapshot" tone="info" />} />
          <div className="chart-summary"><div><strong>78,6 %</strong><MetricDelta>+12,4 PP</MetricDelta></div><span>Zielwert 90 %</span></div>
          <div className="line-chart" aria-label="Readiness steigt in zwölf Wochen von 61 auf 79 Prozent">
            <div className="chart-y-labels"><span>100 %</span><span>75 %</span><span>50 %</span><span>25 %</span></div>
            <svg viewBox="0 0 605 92" preserveAspectRatio="none" role="img">
              <defs><linearGradient id="readinessFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#5B9BE6" stopOpacity=".35"/><stop offset="100%" stopColor="#5B9BE6" stopOpacity="0"/></linearGradient></defs>
              <line x1="0" y1="22" x2="605" y2="22" className="chart-target" />
              <polygon points={`0,92 ${chartPoints} 605,92`} fill="url(#readinessFill)" />
              <polyline points={chartPoints} className="chart-line" />
              <circle cx="605" cy="12" r="4" className="chart-dot" />
            </svg>
            <div className="chart-x-labels"><span>20. Mai</span><span>17. Juni</span><span>15. Juli</span><span>8. Aug.</span></div>
          </div>
          <div className="chart-legend"><span><i className="legend-primary" />Einreichungsbereit</span><span><i className="legend-dashed" />Zielwert</span></div>
        </div>

        <div className="panel evidence-ring-panel">
          <SectionHeader title="Evidence Coverage" description="Über aktive Zyklen" />
          <div className="ring-wrap">
            <div className="progress-ring" style={{ '--progress': '93deg' } as React.CSSProperties}><div><strong>93%</strong><span>vollständig</span></div></div>
          </div>
          <div className="evidence-breakdown">
            <div><span><i className="dot success" />Vollständig</span><strong>1.467</strong></div>
            <div><span><i className="dot warning" />Ausstehend</span><strong>98</strong></div>
            <div><span><i className="dot danger" />Fehlend</span><strong>14</strong></div>
          </div>
          <Button variant="ghost" onClick={() => navigate('/evidence')}>Evidence öffnen <ArrowRight size={15} /></Button>
        </div>
      </section>

      <section className="panel deadline-panel">
        <SectionHeader title={`Nächste Fristen · ${horizon} Tage`} description={`${filteredReports.length} aktive Meldungen über alle Regimes`} action={<Button variant="ghost" onClick={() => navigate('/cycles')}>Alle Zyklen <ArrowRight size={15} /></Button>} />
        <div className="deadline-timeline">
          {filteredReports.slice(0, 5).map((report, index) => (
            <button key={report.id} className="deadline-item" onClick={() => navigate(`/inventory?report=${report.id}`)}>
              <div className={cx('deadline-date', index < 2 && 'date-urgent')}><strong>{formatDate(report.deadline).split(' ')[0]}</strong><span>{formatDate(report.deadline).split(' ')[1]}</span></div>
              <div className="deadline-line"><span /></div>
              <div className="deadline-info"><span className="regime-tag">{report.regime}</span><strong>{report.name}</strong><small>{report.entity}</small></div>
              <div className="deadline-progress"><ProgressBar value={report.progress} tone={report.status === 'Blocked' ? 'danger' : report.status === 'Ready' ? 'success' : 'info'} /><span>{report.progress} %</span></div>
              <StatusChip label={report.status} />
              <ChevronRight size={16} />
            </button>
          ))}
        </div>
      </section>

      <section className="overview-lower-grid">
        <div className="panel blockers-panel">
          <SectionHeader title="Kritische Blocker" description="Nach Materialität und Frist priorisiert" action={<Button variant="ghost" onClick={() => navigate('/validation')}>Alle Findings <ArrowRight size={15} /></Button>} />
          <div className="compact-table" role="table" aria-label="Kritische Findings">
            {blocking.map((finding) => (
              <button role="row" key={finding.id} onClick={() => setSelectedFinding(finding)}>
                <span role="cell" className={cx('severity-bar', `severity-${finding.severity}`)} />
                <span role="cell" className="finding-main"><strong>{finding.title}</strong><small>{finding.ruleId} · {finding.report}</small></span>
                <span role="cell"><StatusChip label={severityLabel[finding.severity]} tone={finding.severity === 'blocking' || finding.severity === 'high' ? 'danger' : 'warning'} compact /></span>
                <span role="cell" className="owner-cell"><Avatar name={finding.owner} size="small" /><span>{finding.owner}</span></span>
                <ChevronRight role="cell" size={15} />
              </button>
            ))}
          </div>
        </div>

        <div className="panel activity-panel">
          <SectionHeader title="Kontrollaktivität" description="Unveränderliche Audit-Ereignisse" />
          <div className="activity-list">
            {data.activity.map((entry) => (
              <div key={entry.id} className="activity-item">
                <span className={cx('activity-node', `node-${entry.tone}`)} />
                <div><strong>{entry.action}</strong><p>{entry.object}</p><small>{entry.actor} · {formatDate(entry.timestamp, true)}</small></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="attention-strip">
        <div className="attention-icon"><Sparkles size={20} /></div>
        <div><strong>3 Regulatory Impacts warten auf fachliche Freigabe</strong><p>REG-2048 betrifft 17 Reporting-Objekte. Vorschläge sind assistiv und werden erst nach unabhängiger Prüfung aktiv.</p></div>
        <Button variant="secondary" onClick={() => navigate('/radar')}>Impact List prüfen</Button>
      </section>

      <Drawer open={Boolean(selectedFinding)} onClose={() => setSelectedFinding(null)} title={selectedFinding?.title ?? ''} description={selectedFinding ? `${selectedFinding.id} · ${selectedFinding.ruleId}` : undefined} footer={<><Button onClick={() => setSelectedFinding(null)}>Schließen</Button><Button variant="primary" onClick={() => navigate('/exceptions')}>Ausnahme bearbeiten</Button></>}>
        {selectedFinding ? <>
          <div className="drawer-hero"><StatusChip label={severityLabel[selectedFinding.severity]} tone="danger" /><StatusChip label={selectedFinding.state} /><span>Pack {selectedFinding.packVersion}</span></div>
          <DetailList items={[
            { label: 'Report', value: selectedFinding.report },
            { label: 'Owner', value: selectedFinding.owner },
            { label: 'Erwartet', value: selectedFinding.expected },
            { label: 'Tatsächlich', value: selectedFinding.actual },
            { label: 'Quelle', value: selectedFinding.source, full: true },
          ]} />
          <div className="callout callout-info"><GitBranch size={17} /><div><strong>Lineage-Kontext verfügbar</strong><p>Quelle, Mapping- und Transformationsversion sind mit diesem Ergebnis verknüpft.</p></div></div>
          <div className="drawer-section"><h3>Empfohlener nächster Schritt</h3><p>{selectedFinding.fixHint}</p></div>
          <div className="drawer-section"><h3>Kontrollkette</h3><ol className="control-steps"><li className="done"><CheckCircle2 size={15} /><span>Snapshot eingefroren<small>Hash b7e4…91ac</small></span></li><li className="done"><GitBranch size={15} /><span>Mapping v5 angewendet<small>deterministischer Run</small></span></li><li className="active"><ShieldAlert size={15} /><span>Finding geöffnet<small>{formatDate(selectedFinding.createdAt, true)}</small></span></li><li><TimerReset size={15} /><span>Remediation ausstehend</span></li></ol></div>
        </> : null}
      </Drawer>
    </div>
  );
}
